'use client'
import { EAnalyticsEventName, trackEvent } from '@repo/shared/adapters'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { Card, Col, Row, Typography, toast } from '@repo/ui/components'
import { useEffect, useRef, useState, useMemo } from 'react'
import { OrderBoxAssetSelect } from './order-box.asset-select'
import { OrderBoxDialog } from './order-box.dialog'
import { OrderBoxForm, type OrderBoxFormRef } from './order-box.form'
import { OrderBoxConvertForm } from './order-box.convert-form'
import { OrderBoxInstantPrice } from './order-box.instant-price'
import { OrderBoxModeSwitch } from './order-box.mode-switch'
import { OrderBoxPriceTypeTabs } from './order-box.price-type-tabs'
import type { ReferralData } from './order-box.referral.types'
import { OrderBoxTabs } from './order-box.tabs'
import type {
  FlatAsset,
  OrderBoxFormValues,
  OrderBoxMode,
  OrderBoxPriceType,
  OrderBoxProps,
  OrderBoxTab,
} from './order-box.types'

export const OrderBox = ({
  // UI Configuration
  mode: initialMode = 'order',
  hideModeSwitch = false,
  hideAssetSelect = false,

  // Tab Management
  tab = 'buy',
  setTab,

  // Price Configuration
  priceType: initialPriceType = 'market',
  instantBuyPrice = 0,
  instantSellPrice = 0,
  isInstantPriceLoading = false,

  // Currency Configuration
  inputCurrency = 'IRR',
  outputCurrency = 'TMN',

  // Asset Management
  selectedAsset,
  onAssetChange,
  availableAssets = [],
  allProjects = [],

  // Balance & Amounts
  availableAmount = 0,
  availableBalance = 0,
  maxIRRAmount = 0,
  maxTokenAmount = 0,
  isBalanceLoading = false,

  // User Data
  userId,

  // API Callbacks
  onPlaceOrder,
  onPayOrder,
  onResetOrderData,

  // API Data
  flatOrderData,
  isPlacingOrder = false,
  isPayingOrder = false,

  // Form Handlers
  onSubmit,

  // List Projects Data
  listProjectsData,

  tokenType = 'all',

  // Search Functionality
  onSearchChange,

  // Default Referral Code
  defaultReferralCode,
  onCheckReferralCode,
  targetScale = 1,
  baseAvailableBalance = 0,

  // Convert Mode Configuration
  fromAsset: initialFromAsset,
  toAsset: initialToAsset,
  onFromAssetChange,
  onToAssetChange,
  onFromAmountChange,
  conversionRate = 0,
  isConversionRateLoading = false,
  onPlaceOrderSuccess,
  convertOrderId,
  convertOrderBaseAmount,
  convertOrderTargetAmount,
  onConvertFinalize,
  onConvertSubmit,
  onRefreshConvertPrice,
  setToAmount,
  setFromAmount,
  fromAmount,
  toAmount,
  isCalculatingConvertAmount = false,
  onCalculateReceivedAmount,
  onCalculateReceivedAmountForToAmount,
  isMobile = false,
  affiliateCode,
  referal,
}: OrderBoxProps) => {
  const [mode, setMode] = useState<OrderBoxMode>(initialMode)
  const [priceType, setPriceType] =
    useState<OrderBoxPriceType>(initialPriceType)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingOrder, setPendingOrder] = useState<OrderBoxFormValues | null>(
    null,
  )

  const [isPriceValid, setIsPriceValid] = useState(true)

  const [assetError, setAssetError] = useState<string | null>(null)

  // Convert mode state
  const [fromAsset, setFromAsset] = useState<FlatAsset | undefined>(
    initialFromAsset,
  )
  const [toAsset, setToAsset] = useState<FlatAsset | undefined>(initialToAsset)

  // Track if referral was manually entered (not from default)
  const [isManualReferral, setIsManualReferral] = useState(false)

  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()

  // Ref for form reset functionality
  const formRef = useRef<OrderBoxFormRef>(null)

  // Refs to track previous values for reset logic
  const prevTabRef = useRef<OrderBoxTab>(tab)
  const prevTokenRef = useRef<string | undefined>(selectedAsset?.token)

  // Helper to get price per token
  const getPrice = (form: OrderBoxFormValues) => {
    if (mode === 'easy') {
      return tab === 'buy' ? instantBuyPrice : instantSellPrice
    }
    if (priceType === 'market') {
      return tab === 'buy' ? instantBuyPrice : instantSellPrice
    }
    return form?.price || 0
  }

  // Helper to get total
  const getTotal = (form: OrderBoxFormValues) => {
    const price = getPrice(form)
    return Number(((form?.amount || 0) * price).toFixed(0))
  }

  // Handler for form submit (show dialog)
  const handleFormSubmit = (
    form: OrderBoxFormValues,
    retryWithoutReferral: boolean = false,
  ) => {
    // Validate asset selection

    if (!selectedAsset) {
      setAssetError(t('order_asset_required'))
      return
    }

    const referralCode =
      // selectedAsset?.token === 'MALEK' || retryWithoutReferral
      selectedAsset?.token === 'MALEK'
        ? ''
        : (_referralCode.current as string) || ''

    onPlaceOrder?.(
      {
        submit: true,
        targetCurrency: selectedAsset?.token || '',
        baseCurrency: inputCurrency,
        amount:
          tab === 'buy'
            ? form.amount * targetScale
            : Number(form.amount.toFixed(0)),
        amountCurrency: selectedAsset?.token,
        orderType: tab === 'buy' ? 'buy' : 'sell',
        affiliateCode,
        ...(referralCode && { referralCode }),
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onSuccess: (response: any) => {
          setAssetError(null)
          setPendingOrder(form)
          setIsPriceValid(true)
          setDialogOpen(true)
        },
        onError: (error: any) => {
          // If this was a request with referral code and it failed, retry without it
          // if (referralCode && !retryWithoutReferral) {
          //   _referralCode.current = 'non-default'
          //   handleFormSubmit(form, true)
          //   return
          // }
          errorHandler(error, {})
        },
      },
    )
  }

  // Handler for dialog confirm
  const handleDialogConfirm = async () => {
    if (!pendingOrder) return

    await onPayOrder?.(
      {
        orderID: flatOrderData?.orderId,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onSuccess: (response: any) => {
          onSubmit?.(pendingOrder)
          toast.success({
            title: t(
              tab === 'buy'
                ? 'order_box_buy_success'
                : 'order_box_sell_success',
              {
                amount: (tab === 'sell'
                  ? pendingOrder.amount / Number(selectedAsset?.scale)
                  : pendingOrder.amount
                ).toLocaleString('en-US', {
                  useGrouping: false,
                  maximumFractionDigits: 20,
                }),
                asset: selectedAsset?.token || '',
              },
            ),
          })

          const eventProperties = {
            user_id_custom: userId,
            transaction_id: response.data.transaction?.id,
            project_name: selectedAsset?.name || '',
            token_symbol:
              response.data.transaction?.order?.targetCurrency || '',
            token_price: getPrice(pendingOrder),
            token_amount: response?.data?.transaction?.targetAmount,
            total_price: response?.data?.transaction?.baseAmount,
            fee: flatOrderData?.otcFee,
            currency: 'IRR',
          }

          trackEvent({
            name:
              tab === 'buy'
                ? EAnalyticsEventName.BuyToken
                : EAnalyticsEventName.SellToken,
            properties: eventProperties,
          })

          setDialogOpen(false)
          setPendingOrder(null)
          setIsPriceValid(false)

          // Reset form after successful operation
          formRef.current?.resetForm()

          // Reset order data to clear total calculation
          onResetOrderData?.()
        },
        onError: errorHandler,
      },
    )
  }

  // Refresh price handler
  const handleRefreshPrice = (referral_code?: string | null) => {
    if (referral_code) {
      _referralCode.current = referral_code
    }
    if (pendingOrder) {
      let referralCode =
        selectedAsset?.token === 'MALEK'
          ? ''
          : (_referralCode.current as string) || ''

      // if (referralCode === 'non-default') {
      //   referralCode = ''
      // }

      onPlaceOrder?.(
        {
          submit: true,
          targetCurrency: selectedAsset?.token || '',
          baseCurrency: inputCurrency,
          amount:
            tab === 'buy'
              ? pendingOrder.amount * targetScale
              : Number(pendingOrder.amount.toFixed(0)),
          amountCurrency: selectedAsset?.token,
          orderType: tab === 'buy' ? 'buy' : 'sell',
          affiliateCode,
          ...(referralCode && { referralCode }),
        },
        {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onSuccess: (response: any) => {
            setIsPriceValid(true)
          },
          onError: errorHandler,
        },
      )
    }
  }

  const _referralCode = useRef<string>(defaultReferralCode || '')

  const handleReferralChange = (
    data: ReferralData | null,
    callback?: () => void,
    forceNoReferral: boolean = false,
  ) => {
    // For convert mode, just update the referral code state
    if (tab === 'convert') {
      if (data) {
        // _referralCode.current = data.code
      } else {
        // When referral code is removed, clear it completely (don't use defaultReferralCode)
        // _referralCode.current = ''
      }
      callback?.()
      return
    }

    // For buy/sell mode, handle as before
    if (pendingOrder) {
      const referralCode =
        selectedAsset?.token === 'MALEK' ? '' : (data?.code as string) || ''

      onPlaceOrder?.(
        {
          submit: true,
          targetCurrency: selectedAsset?.token || '',
          baseCurrency: inputCurrency,
          amount:
            tab === 'buy'
              ? pendingOrder.amount * targetScale
              : Number(pendingOrder.amount.toFixed(0)),
          amountCurrency: selectedAsset?.token,
          orderType: tab === 'buy' ? 'buy' : 'sell',
          affiliateCode,
          ...(referralCode && !forceNoReferral && { referralCode }),
        },
        {
          onSuccess: () => {
            setIsPriceValid(true)
            if (!forceNoReferral) {
              // _referralCode.current = referralCode
              // Mark as manual referral if data is provided, otherwise reset
              setIsManualReferral(!!data)
              callback?.()
            }
          },
          onError: (error: any) => {
            handleReferralChange(data, callback, true)
            errorHandler(error, {})
          },
        },
      )
    }
  }

  // Handle asset change and clear error
  const handleAssetChange = (asset: FlatAsset) => {
    _referralCode.current = ''
    setAssetError(null)
    onAssetChange?.(asset)
    // Reset order data (including fee) when asset changes
    onResetOrderData?.()
  }

  const handleTimeExpired = () => {
    setIsPriceValid(false)
  }

  useEffect(() => {
    if (selectedAsset?.token === 'MALEK' && tab === 'buy') {
      onAssetChange?.(null as any)
    }
  }, [tab, selectedAsset?.token])

  // Reset order data when tab or selected asset changes
  useEffect(() => {
    const tabChanged = prevTabRef.current !== tab
    const tokenChanged = prevTokenRef.current !== selectedAsset?.token

    if (tabChanged || tokenChanged) {
      onResetOrderData?.()
      prevTabRef.current = tab
      prevTokenRef.current = selectedAsset?.token
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedAsset?.token])

  // Convert mode handlers
  const handleFromAssetChange = (asset: FlatAsset) => {
    setFromAsset(asset)
    onFromAssetChange?.(asset)

    setFromAmount?.('0')
    setToAmount?.('0')
  }

  const handleToAssetChange = (asset: FlatAsset) => {
    setToAsset(asset)
    onToAssetChange?.(asset)
    setFromAmount?.('0')
    setToAmount?.('0')
  }

  const handleSwapAssets = () => {
    const tempFrom = fromAsset
    const tempTo = toAsset
    const tempToAmount = toAmount // Current received amount becomes new payment amount

    setFromAsset(tempTo)
    setToAsset(tempFrom)
    onFromAssetChange?.(tempTo as FlatAsset)
    onToAssetChange?.(tempFrom as FlatAsset)

    // Use current toAmount as new fromAmount and calculate new toAmount
    setTimeout(() => {
      setFromAmount?.(tempToAmount || 0)
      setToAmount?.(0)

      // Trigger API calculation after swap with the new fromAmount
      if (tempToAmount && tempToAmount > 0 && onCalculateReceivedAmount) {
        onCalculateReceivedAmount(tempToAmount, true)
      }
    }, 0)
  }

  // Wrapper for convert submit that handles referral code
  const handleConvertSubmitWithReferral = (
    fromAmount: number,
    referralCode?: string,
  ) => {
    if (onConvertSubmit) {
      // Use provided referral code or fall back to ref value
      const finalReferralCode = referralCode || _referralCode.current || ''
      onConvertSubmit(fromAmount, finalReferralCode)
    }
  }

  // Get from asset balance
  const fromAssetBalance = useMemo(() => {
    if (!fromAsset) return 0
    const account = availableAssets.find(
      (a) => a.token === fromAsset.token || a.id === fromAsset.id,
    )
    return account?.availableBalance || account?.balance || 0
  }, [fromAsset, availableAssets])

  return (
    <div className='!max-w-[688px] w-full px-md sm:px-0 mx-0 sm:mx-auto'>
      <Card withoutBorder={hideModeSwitch} includeShadow={!hideModeSwitch}>
        {!hideModeSwitch && (
          <Row className='mb-md'>
            <Col>
              <OrderBoxModeSwitch mode={mode} onChange={setMode} />
            </Col>
          </Row>
        )}
        <Row className='mb-md'>
          <Col>
            <OrderBoxTabs tab={tab} onTabChange={setTab} />
          </Col>
        </Row>
        {mode === 'order' && (
          <Row className='mb-md'>
            <Col>
              <OrderBoxPriceTypeTabs
                priceType={priceType}
                onChange={setPriceType}
              />
            </Col>
          </Row>
        )}
        {onAssetChange && !hideAssetSelect && tab !== 'convert' && (
          <Row className='mb-md'>
            <Col className='gap-2xs'>
              <OrderBoxAssetSelect
                selectedAsset={selectedAsset}
                onChange={handleAssetChange}
                error={assetError || undefined}
                containerClassName='bg-neutral-surface border border-neutral-surface-5'
                projects={
                  tab === 'sell' && availableAssets
                    ? availableAssets
                    : allProjects
                }
                tab={tab}
                listProjectsData={listProjectsData}
                onSearchChange={onSearchChange}
                tokenType={tokenType}
              />
              {assetError && (
                <Typography
                  size='xs'
                  color='text-negative-surface-100'
                  className='mt-sm'
                  weight='regular'
                >
                  {assetError}
                </Typography>
              )}
            </Col>
          </Row>
        )}
        {tab === 'convert' ? (
          <OrderBoxConvertForm
            fromAsset={fromAsset}
            toAsset={toAsset}
            onFromAssetChange={handleFromAssetChange}
            onToAssetChange={handleToAssetChange}
            onSwapAssets={handleSwapAssets}
            fromAmount={fromAmount || 0}
            toAmount={toAmount || 0}
            onFromAmountChange={onFromAmountChange || (() => {})}
            onToAmountChange={setToAmount || (() => {})}
            fromBalance={fromAssetBalance}
            toBalance={0}
            conversionRate={conversionRate}
            isConversionRateLoading={isConversionRateLoading}
            availableAssets={availableAssets}
            allProjects={allProjects}
            listProjectsData={listProjectsData}
            onSearchChange={onSearchChange}
            onSubmit={handleConvertSubmitWithReferral}
            isLoading={isPlacingOrder}
            targetScale={targetScale}
            isBalanceLoading={isBalanceLoading}
            onPlaceOrderSuccess={onPlaceOrderSuccess}
            convertOrderId={convertOrderId}
            convertOrderBaseAmount={convertOrderBaseAmount}
            convertOrderTargetAmount={convertOrderTargetAmount}
            onConvertFinalize={onConvertFinalize}
            isCalculatingConvertAmount={isCalculatingConvertAmount}
            onCalculateReceivedAmount={onCalculateReceivedAmount}
            onCalculateReceivedAmountForToAmount={
              onCalculateReceivedAmountForToAmount
            }
            defaultReferralCode={defaultReferralCode}
            flatOrderData={flatOrderData}
            onReferralChange={handleReferralChange}
            onCheckReferralCode={onCheckReferralCode}
            isPriceValid={isPriceValid}
            onTimeExpired={handleTimeExpired}
            onRefreshPrice={handleRefreshPrice}
            onRefreshConvertPrice={
              onRefreshConvertPrice
                ? (fromAmountParam, referralCodeParam) => {
                    // Call the provided handler with success callback to update isPriceValid
                    onRefreshConvertPrice(
                      fromAmountParam,
                      referralCodeParam,
                      () => {
                        setIsPriceValid(true)
                      },
                    )
                  }
                : undefined
            }
            isMobile={isMobile}
            selectedAsset={selectedAsset}
            referal={referal}
          />
        ) : (
          <>
            {mode === 'easy' && (
              <OrderBoxInstantPrice
                price={tab === 'buy' ? instantBuyPrice : instantSellPrice}
                tab={tab}
                inputCurrency={inputCurrency}
                outputCurrency={outputCurrency}
                isLoading={isInstantPriceLoading}
                selectedAsset={selectedAsset}
                isInstantPriceLoading={isInstantPriceLoading}
              />
            )}
            <OrderBoxForm
              ref={formRef}
              key={`${selectedAsset?.token || ''}-${tab}`}
              tab={tab}
              priceType={priceType}
              availableAmount={availableAmount}
              availableBalance={availableBalance}
              baseAvailableBalance={baseAvailableBalance}
              onSubmit={handleFormSubmit}
              mode={mode}
              isLoading={isPlacingOrder}
              inputCurrency={inputCurrency}
              outputCurrency={outputCurrency}
              token={selectedAsset?.token || ''}
              selectedAsset={selectedAsset}
              instantPrice={tab === 'buy' ? instantBuyPrice : instantSellPrice}
              maxIRRAmount={maxIRRAmount}
              maxTokenAmount={maxTokenAmount}
              targetScale={targetScale}
              onOtcPlaceOrder={onPlaceOrder}
              flatOrderData={flatOrderData}
              tradeStatus={selectedAsset?.tradeStatus}
              setTab={setTab}
              minOrderSize={selectedAsset?.minOrderSize}
              isBalanceLoading={isBalanceLoading}
              isInstantPriceLoading={isInstantPriceLoading}
              affiliateCode={affiliateCode}
              userId={userId}
            />
          </>
        )}
      </Card>
      {/* Confirmation Dialog */}
      {pendingOrder && selectedAsset && (
        <OrderBoxDialog
          referal={referal}
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open)
            if (!open) {
              setPendingOrder(null)
              setIsPriceValid(false)
              _referralCode.current = ''
              setIsManualReferral(false)
            }
          }}
          tab={tab}
          assetTitle={selectedAsset.name || ''}
          assetId={selectedAsset.token || ''}
          price={getPrice(pendingOrder)}
          amount={pendingOrder.amount}
          targetScale={targetScale}
          total={getTotal(pendingOrder)}
          onConfirm={handleDialogConfirm}
          isPriceValid={isPriceValid}
          onTimeExpired={handleTimeExpired}
          isLoading={isPayingOrder}
          isPlacingOrder={isPlacingOrder}
          onRefreshPrice={handleRefreshPrice}
          onReferralChange={handleReferralChange}
          finalPayableAmount={getTotal(pendingOrder)}
          flatOrderData={flatOrderData}
          hasDefaultReferral={!!defaultReferralCode}
          isManualReferral={isManualReferral}
          isCrypto={selectedAsset?.kind === 'crypto'}
        />
      )}
    </div>
  )
}
